// @flow

import * as React from "react";
import { useSelector, useDispatch } from "react-redux";

import LoadPayPalSDK from "../../utils/LoadPayPalSDK";
import { paypalApproveOrder, paypalPaidOrder } from "../../api/cart";
import { createPaypalOrder, checkoutOrder } from "../../actions/cart";
import useAsyncState from "../../hooks/useAsyncState";
import ServerErrorMessage from "./ServerErrorMessage";
import LoaderCover from "./Loader";

type PropsType = { onSuccess: Function };

const PayPalButton = (props: PropsType): React.Node => {
    const { nonce } = useSelector((state: any): any => state.user);
    const dispatch = useDispatch();
    const containerRef = React.useRef(null);
    const [state, localActions] = useAsyncState();
    const [loaded, setLoaded] = React.useState(false);

    React.useEffect(() => {
        LoadPayPalSDK().then(() => {
            initPaypalOrder();
        });
    }, []); // eslint-disable-line

    const initPaypalOrder = () => {
        if (window.paypal && containerRef.current && !loaded) {
            renderbuttons();
            setLoaded(true);
        } else {
            console.error("It looks like the PayPal JS SDK is not loaded.");
        }
    };

    const renderbuttons = (): any => {
        window.paypal
            .Buttons({
                createOrder: async (data, actions) => {
                    // Step 1: create a pending WC order via /cart/checkout
                    let checkoutOrderResponse;
                    try {
                        checkoutOrderResponse = await dispatch(
                            checkoutOrder("ppcp-gateway")
                        );
                    } catch (error) {
                        localActions.fail(
                            error.message ||
                                "There was an error initializing PayPal."
                        );
                        return null;
                    }

                    if (!checkoutOrderResponse || !checkoutOrderResponse.order_id) {
                        localActions.fail("There was an error initializing PayPal.");
                        return null;
                    }

                    let paypalOrderObject = await dispatch(
                        createPaypalOrder(
                            checkoutOrderResponse.order_id,
                            checkoutOrderResponse.order_key
                        )
                    ).catch(error => {
                        localActions.fail(
                            error.message ||
                                "There was an error initializing PayPal."
                        );
                        return null;
                    });

                    // PPCP v3.4.0+: backend returns { id, custom_id } directly
                    if (paypalOrderObject && paypalOrderObject.id) {
                        return paypalOrderObject.id;
                    } else {
                        localActions.fail(
                            paypalOrderObject && paypalOrderObject.message
                                ? paypalOrderObject.message
                                : "There was an error initializaing PayPal."
                        );
                        return null;
                    }
                },
                onApprove: function (data, actions) {
                    const paypalOrderId = data.orderID;
                    console.log(data);
                    localActions.request();
                    // approve order - tells back end to approve purhcase order
                    paypalApproveOrder(
                        nonce["ppc-approve-order"],
                        paypalOrderId
                    )
                        .then(response => {
                            if (response.success) {
                                // paid order (marks as paid in woocommerce)
                                return paypalPaidOrder(paypalOrderId).then(
                                    checkoutSuccessResponse => {
                                        // onSuccess
                                        if (
                                            props.onSuccess &&
                                            checkoutSuccessResponse.is_paid
                                        ) {
                                            props.onSuccess(
                                                checkoutSuccessResponse
                                            );
                                            localActions.success();
                                        } else {
                                            localActions.fail(
                                                "Failed to approve PayPal order."
                                            );
                                        }
                                    }
                                );
                            } else {
                                localActions.fail(
                                    response.data
                                        ? response.data.message
                                        : "Failed to approve PayPal order."
                                );
                            }
                        })
                        .catch(error => {
                            console.error(error);
                            localActions.fail(
                                error.message ||
                                    "There was an issue approving your order with PayPal."
                            );
                        });
                },
                // button visual styles
                style: {
                    layout: "horizontal",
                    color: "blue",
                    shape: "rect",
                    label: "pay",
                    tagline: false,
                    height: 55,
                },
            })
            .render(containerRef.current);
    };

    return (
        <>
            <LoaderCover fixed={true} active={state.loading} />

            <div className="paypal-button center-xs">
                <div ref={containerRef}></div>

                {/* {state.loading ? <DotsLoader fill="#008731" /> : null} */}

                <ServerErrorMessage>{state.error}</ServerErrorMessage>
            </div>
        </>
    );
};

export default PayPalButton;
